/* eslint-disable @typescript-eslint/no-explicit-any */

import 'katex/dist/katex.min.css';
import 'react-photo-view/dist/react-photo-view.css';

import { Box, Loader, Tooltip, Transition } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons';
import cx from 'clsx';
import mixpanel from 'mixpanel-browser';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { CodeProps } from 'react-markdown/lib/ast-to-react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import RehypeHighlight from 'rehype-highlight';
import RehypeKatex from 'rehype-katex';
import RemarkBreaks from 'remark-breaks';
import RemarkGfm from 'remark-gfm';
import RemarkMath from 'remark-math';
// @ts-ignore
import LinkifyIt from 'linkify-it';

import { useChatStore } from '@mygpt/app/chat';
import { MessageTools } from '@mygpt/app/chat/components/MessageTools';
import { checkIsUrl, checkObjectType, comprehensiveUrlRegex, UrlProcessor } from '@mygpt/app/utils';
import { Footnote, ReferenceSign } from '@mygpt/types';

import classes from './markdown.module.css';

const CodeComponent = ({ children, className, inline, node, ...props }: CodeProps) => {
  const match = /language-(\w+)/.exec(className || '');
  const [code, setCode] = useState('');
  const ref = useRef<HTMLPreElement>(null);
  const lang = match?.[1] != null ? match[1] : 'unknown';
  const isHtml = lang === 'html';
  const isCss = lang === 'css';
  const isJs = ['javascript', 'typescript', 'js', 'jsx', 'tsx'].includes(lang.toLowerCase());

  useLayoutEffect(() => {
    setCode(ref.current?.innerText ?? '');
  }, [children]);

  // CodePen all options: https://blog.codepen.io/documentation/api/prefill/
  const data: Record<string, string> = {
    title: `Code from gbase.ai`,
  };
  if (isHtml) {
    data['html'] = code;
  } else if (isCss) {
    data['css'] = code;
  } else if (isJs) {
    data['js'] = code;
  }

  const isFrontEndCode = isHtml || isCss || isJs;

  return !inline ? (
    <>
      <div className="flex justify-between items-center text-gray-200 dark:text-gray-400 bg-dark-[#334155] px-4 py-2 text-xs font-sans rounded-t-md">
        <span>{lang !== 'unknown' && lang}</span>
        <div className="flex space-x-2 items-center">
          {isFrontEndCode && (
            <form action="https://codepen.io/pen/define" method="POST" target="_blank">
              <input type="hidden" name="data" value={JSON.stringify(data)} />
              <button type="submit">
                <IconExternalLink size={16} className="inline align-text-bottom" />
              </button>
            </form>
          )}

          <MessageTools content={() => code} className="text-gray-200 hover:text-white" />
        </div>
      </div>
      <div className="overflow-y-auto">
        <code className={cx(classes['code'], className)} ref={ref}>
          {children}
        </code>
      </div>
    </>
  ) : (
    <code className={cx(classes['code'], className)} {...props}>
      {children}
    </code>
  );
};

const ImageComponent = ({
  alt,
  src,
}: React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>) => {
  let width: number | undefined = undefined;
  let height: number | undefined = undefined;
  const match = src?.match(/wh=([\d.]+)x([\d.]+)/);

  if (match) {
    width = parseInt(match[1]);
    height = parseInt(match[2]);
  }

  return (
    <PhotoView src={src}>
      <img src={src} alt={alt} width={width} height={height} style={{ cursor: 'pointer' }} />
    </PhotoView>
  );
};

const linkify = new LinkifyIt();

const extractLinks = (text: string): { url: string; text: string }[] => {
  const links = linkify.match(text);
  if (links) {
    return links.map((link: { url: string; text: string }) => ({
      url: link.url,
      text: link.text,
    }));
  }
  return [];
};

const LinkComponent = ({
  children,
  href,
}: React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>) => {
  const { pageSource } = useChatStore();
  const isWidget = pageSource === 'widget';

  // 使用 extractLinks 提取链接
  const links = extractLinks(children?.toString() || '');

  // 检查是否是邮箱地址
  const isEmail = href?.startsWith('mailto:');
  // 检查是否是电话号码
  const isTel = href?.startsWith('tel:');

  if (isEmail || isTel) {
    return (
      <a href={href} target="_blank" rel="noreferrer">
        {children}
      </a>
    );
  }

  // 如果提取到链接，处理链接
  if (links.length > 0) {
    return (
      <>
        {links.map((link: { url: string; text: string }, index: number) => (
          <a key={index} href={link.url} target="_blank" rel="noreferrer">
            {link.text}
          </a>
        ))}
      </>
    );
  }

  let width: number | undefined = undefined;
  let height: number | undefined = undefined;
  const match = href?.match(/wh=([\d.]+)x([\d.]+)/);
  // 兼容解析图片导致感叹号变成中文了的问题（会变成链接了）
  if (match) {
    width = parseInt(match[1]);
    height = parseInt(match[2]);

    return <img src={href} alt={children?.toString()} width={width} height={height} />;
  }

  // 处理链接和文字粘在一起的问题
  const childrenContent = children ? (children as Array<string>)[0] : '';
  if (children && checkObjectType(children) === 'Array' && childrenContent !== href) {
    const urlMatch =
      childrenContent.length > 200
        ? /https?:\/\/[-a-zA-Z0-9@:%._+~#=]{2,256}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/g
        : new RegExp(comprehensiveUrlRegex, 'gi');
    let matchResult = urlMatch.exec(childrenContent);
    let componentArr: { type: 'text' | 'link'; content: string; href?: string }[] = [];

    for (; matchResult !== null; ) {
      // 匹配不上或者完全匹配只有url就跳过
      if (!matchResult || matchResult[0] === matchResult.input) break;
      const [url, urlProtocolMatched] = matchResult;
      const startStr = matchResult.input.substring(0, matchResult.index);
      const urlProcessor = new UrlProcessor(url, urlProtocolMatched);
      const endStr = matchResult.input.substring(matchResult.index + matchResult[0].length);

      matchResult = urlMatch.exec(endStr);

      componentArr = [
        ...componentArr,
        { type: 'text', content: startStr },
        { type: 'link', content: urlProcessor.getAnchorText(), href: urlProcessor.getAnchorHref() },
        { type: 'text', content: matchResult ? '' : endStr },
      ];
    }

    if (componentArr.length) {
      return (
        <>
          {componentArr.map((item, index) => {
            if (item.type === 'link') {
              return (
                <a key={index} href={item.href} target="_blank" rel="noreferrer">
                  {item.content}
                </a>
              );
            }
            return item.content;
          })}
        </>
      );
    }
  }

  const url = href && checkIsUrl(href) ? new URL(href) : '';
  if (isWidget && url) {
    const searchParams = url.searchParams;

    if (!searchParams.has('fg')) {
      if (searchParams.toString()) {
        // 如果已有其他查询参数，则添加&fg=1
        searchParams.append('fg', '1');
      } else {
        // 如果没有任何查询参数，则添加?fg=1
        searchParams.set('fg', '1');
      }
    }
    // 更新URL
    url.search = searchParams.toString();
  }

  return (
    <a
      href={url.toString()}
      target="_blank"
      rel="noreferrer"
      onClick={() => {
        isWidget && mixpanel.track('link_clicked', { link: href });
      }}
    >
      {children ? children : href}
    </a>
  );
};

const SupComponent = ({ children, ...props }: { children: React.ReactNode; node?: Record<string, any> }) => {
  let footnoteLabel: { id: string; index: number } | null = null;
  const as = props.node?.['children'].filter((node: any) => node.tagName === 'a') ?? [];
  if (as.length) {
    for (const a of as) {
      const dataFootnoteRef = a.properties.dataFootnoteRef;
      if (!dataFootnoteRef) continue;
      const id = a.properties.id;
      const text = a.children.find((node: any) => node.type === 'text');
      if (!text) continue;
      const index = parseInt(text.value);
      footnoteLabel = { id, index };
      break;
    }
  }
  const { footnotes, isReferenceList, referenceList } = React.useContext(FootnoteContext);
  if (isReferenceList || referenceList.length > 0) return null;
  const renderDefault = () => <sup {...props}>{children}</sup>;
  if (!footnoteLabel) return renderDefault();
  const footnote = footnotes.find((footnote) => footnote.index === footnoteLabel?.index);
  if (!footnote) return renderDefault();
  return (
    <a className="no-underline" target="_blank" href={footnote.source} rel="noreferrer" id={footnoteLabel.id}>
      <sup className={classes['footnoteLabel']}>{footnoteLabel.index}</sup>
    </a>
  );
};

const FootnoteItemComponent = ({ index, item }: { item: Record<string, any>; index: number }) => {
  const { id, page_numbers, source, title } = item;
  const label = title || source;

  const { pageSource } = useChatStore();
  const isWidget = pageSource === 'widget';

  const { clickReference } = React.useContext(ReferenceContext);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // 这里检测URL时，必须检测前缀协议，因为source字段可能是文件名，那样没办法区分。
    if (checkIsUrl(source, true)) {
      isWidget &&
        mixpanel.track('reference_content_clicked', {
          source,
          title: labelText,
        });
    } else {
      event.preventDefault();

      if (clickReference && item['file_id'] && item['page_numbers']) {
        clickReference(item['file_id'] || '', item['page_numbers']);
        //统计
        isWidget &&
          mixpanel.track('reference_content_clicked', {
            source,
            page_numbers: item['page_numbers'],
            file_id: item['file_id'] || '',
            title: labelText,
          });
      }
    }
  };
  const { t } = useTranslation();
  // 先隐藏页码
  const page_message = !checkIsUrl(source, true) && page_numbers && page_numbers.length > 0;
  const page_text =
    page_message && page_numbers.length > 1
      ? `${page_numbers[0]}-${page_numbers[page_numbers.length - 1]}`
      : page_numbers[0];
  const labelText = `${label}${page_message ? t('completions.page_of', { page: page_text }) : ''}`;
  return (
    <Tooltip multiline label={labelText} key={id}>
      <span
        className={cx(
          'px-1 leading-6 flex bg-gray-100 text-gray-500 opacity-50 hover:opacity-100 rounded-md text-xs no-underline',
          checkIsUrl(source, true) ? classes['itemUnderline'] : classes['item']
        )}
      >
        <a
          target="_blank"
          href={checkIsUrl(source) ? source : undefined}
          rel="noreferrer"
          data-id={id}
          className="block text-ellipsis whitespace-nowrap overflow-hidden max-w-[240px] no-underline cursor-pointer"
          onClick={handleClick}
        >
          {`${index + 1}. ${labelText}`}
        </a>
      </span>
    </Tooltip>
  );
};

const FootnoteComponent = ({
  children,
  ...props
}: {
  children: React.ReactNode;
  'data-footnotes'?: boolean;
  node?: Record<string, any>;
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<Record<string, any>[]>([]);
  const isLoaded = useRef(false);
  const { pageSource } = useChatStore();
  const isWidget = pageSource === 'widget';
  const { isDigitalHuman, referenceList } = React.useContext(FootnoteContext);

  // 如果是数字人，不显示脚注
  if (isDigitalHuman) {
    return null;
  }

  const getData = useCallback(() => {
    // ol li根据脚注而来
    if (!props.node) return [];
    const list = props.node['children'].filter((node: any) => node.tagName === 'ol');
    if (!list.length) return [];
    const data: Record<string, any>[] = [];
    for (let i = 0; i < list.length; i++) {
      const lis = list[i].children.filter((node: any) => node.tagName === 'li');
      for (let j = 0; j < lis.length; j++) {
        const li = lis[j];
        const id = li.properties.id;
        const p = li.children.find((node: any) => node.tagName === 'p');
        if (!p) continue;
        const a = p.children.find((node: any) => node.tagName === 'a' && node.properties.dataFootnoteBackref);
        if (!a) continue;
        const text = p.children.find((node: any) => node.type === 'text');
        const value = text ? text.value.trim() : '';
        if (value === 'source_button') {
          data.push({ id: '999', source: '', title: '' });
          break;
        }
        if (value === 'show_none') {
          data.push({ id: '9999', source: '', title: '' });
          break;
        }
        const href = a.properties.href;

        // #user-content-fnref-1
        const index = parseInt(href.split('-').pop());
        const reference = referenceList.find((_, _index) => _index === index);
        if (!reference) continue;
        if (
          reference.page_numbers &&
          reference.page_numbers.length > 0 &&
          data.some(
            (v) =>
              v['file_id'] === reference.file_id &&
              v['page_numbers'] &&
              v['page_numbers'].length > 0 &&
              v['page_numbers'][0] === reference.page_numbers[0]
          )
        ) {
          continue;
        }
        if (
          (checkIsUrl(reference.source, true) || reference.page_numbers.length === 0) &&
          data.some((item) => item['source'] === reference.source)
        )
          continue;
        const oldReference = reference as any;
        data.push({
          id,
          file_id: reference.file_id,
          source: reference.source || oldReference['value'],
          title: reference.title || oldReference['value'],
          page_numbers: reference.page_numbers || [],
        });
      }
    }
    return data;
  }, [props.node, referenceList]);

  useLayoutEffect(() => {
    setData(getData());
  }, [getData]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const { getReference } = React.useContext(ReferenceContext);

  const handleGetSource = () => {
    if (loading || !getReference) return;
    setLoading(true);

    isLoaded.current = true;
    getReference()
      .then((items) => {
        setData((currentData) => {
          const newData = [...currentData.filter((v) => v['id'] !== '999' && v['id'] !== '9999')];
          items.forEach((item) => {
            const reference = referenceList.find((reference) => reference.id === item.id);

            if (reference) {
              if (
                reference.page_numbers &&
                reference.page_numbers.length > 0 &&
                newData.some(
                  (v) =>
                    v['page_numbers'] &&
                    v['page_numbers'].length > 0 &&
                    v['page_numbers'][0] === reference.page_numbers[0]
                )
              ) {
                return;
              }
              if (
                (checkIsUrl(reference.source, true) || reference.page_numbers.length === 0) &&
                newData.some((item) => item['source'] === reference.source)
              )
                return;
              newData.push({
                id: item.id,
                title: item.title || reference.title,
                source: reference.source,
                page_numbers: reference.page_numbers,
              });
            }
          });
          return newData;
        });
      })
      .finally(() => {
        setLoading(false);
        setVisible(true);
      });
  };

  const mounted = useMemo(() => {
    return data.length > 0 && data[0]['id'] !== '999' && data[0]['id'] !== '9999';
  }, [data]);

  const needFetch = useMemo(() => {
    return !mounted && data.length > 0 && (data[0]['id'] === '999' || data[0]['id'] === '9999');
  }, [data, mounted]);

  const renderEmpty = () => {
    return (
      <div
        key="no_source"
        className="flex px-1 leading-6 items-center bg-gray-100 text-gray-500 rounded-md text-[10px]"
      >
        {t('completions.no_source')}
      </div>
    );
  };
  if (props['data-footnotes'] && props.node) {
    const list = props.node['children'].filter((node: any) => node.tagName === 'ol');
    if (!list.length) return null;

    const handleClick = () => {
      if (needFetch) {
        isWidget && mixpanel.track('reference_button_clicked');
        handleGetSource();
      } else {
        setVisible(!visible);
      }
    };

    if (data.length > 0 && data[0]['id'] === '9999' && !isLoaded.current) return null;
    if (needFetch) {
      if (!getReference || (!loading && isLoaded.current))
        return <div className="flex flex-wrap gap-2">{renderEmpty()}</div>;
    }

    return (
      <div className="flex flex-wrap gap-2">
        <Box className="cursor-pointer opacity-50 hover:opacity-100 text-xs leading-6 h-6" onClick={handleClick}>
          {!loading && (
            <span className="flex items-center">
              {t('completions.get_source')}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="#2c3e50"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                <path d="M9 6l6 6l-6 6" />
              </svg>
            </span>
          )}
          {loading && <Loader size="xs" variant="dots" />}
        </Box>
        {data.length > 0 && mounted && (
          <Transition mounted={visible} transition="fade" duration={400} timingFunction="ease">
            {(styles) => (
              <div className="flex flex-wrap gap-2" style={styles}>
                {data
                  .filter(
                    (item) => item['id'] !== '9999' && item['id'] !== '999' && (!!item['title'] || !!item['source'])
                  )
                  .map((item, index) => (
                    <FootnoteItemComponent key={index} item={item} index={index} />
                  ))}
              </div>
            )}
          </Transition>
        )}
        {data.length === 0 && mounted && renderEmpty()}
      </div>
    );
  }
  return <section {...props}>{children}</section>;
};

const FootnoteContext = React.createContext<{
  footnotes: Footnote[];
  referenceList: Footnote[];
  isReferenceList: boolean;
  isDigitalHuman?: boolean;
}>({
  footnotes: [],
  referenceList: [],
  isReferenceList: false,
  isDigitalHuman: false,
});
type GetReference = () => Promise<ReferenceSign[]>;
const ReferenceContext = React.createContext<{
  getReference?: () => Promise<ReferenceSign[]>;
  clickReference?: (fileId: string, pageNumbers: number[]) => void;
}>({} as any);

export function Markdown(props: {
  content: string;
  footnotes?: Footnote[];
  className?: string;
  referenceList?: Footnote[];
  referenceIndexes?: (ReferenceSign | number)[];
  getReference?: GetReference;
  clickReference?: (fileId: string, pageNumbers: number[]) => void;
  isDigitalHuman?: boolean;
}) {
  const { clickReference, getReference } = props;
  let { referenceIndexes = [], referenceList = [] } = props;
  if (!referenceIndexes) referenceIndexes = [];
  if (!referenceList) referenceList = [];
  const isReferenceList = referenceList.length > 0 && referenceIndexes.length > 0;
  const footnotes = useMemo(() => {
    // 有脚注，直接返回
    if (props.footnotes?.length) return props.footnotes;
    // 兼容分开传递的脚注
    if (isReferenceList) {
      return referenceIndexes
        .map((reference_index) => {
          const ref_index =
            typeof reference_index === 'number'
              ? reference_index
              : referenceList.findIndex((item) => item.id === reference_index.id);
          const reference = referenceList.find((_, index) => index === ref_index);
          if (ref_index === -1) {
            return {
              id: '9999',
              index: 9999,
              title: '',
              source: 'show_none',
              page_numbers: [],
            };
          }
          if (reference) {
            return {
              id: reference.id,
              index: ref_index,
              title: reference.title,
              source: reference.source,
              page_numbers: reference.page_numbers,
            };
          }
          return null;
        })
        .filter((item) => !!item) as Footnote[];
    } else if (referenceList.length > 0) {
      // 如果没有现成的柱脚，就生成一个按钮
      return [{ id: '999', file_id: null, index: 999, source: '', title: 'source_button', page_numbers: [] }];
    }
    return [];
  }, [props.footnotes, isReferenceList, referenceIndexes, referenceList]);
  let content = props.content;
  // 兼容解析图片导致感叹号变成中文了的问题（会变成链接了）
  content = content?.replace(/(!|！)(\[|【)(.*?)(\]|】)(\(|（)(.*?)(\)|）)/g, (...args) => {
    return `![${args[3].replace(/\n|\r\n/g, '')}](${args[6].replace(/\n|\r\n/g, '')})`;
  });
  // 兼容解析文本链接（https://www.xxx.com）adfdf 全角括号或者括号后面没有空格导致把括号后面的内容也做成一部分了
  content = content?.replace(
    /([(（])(https?(:|\\*:)\/\/[^(（)）\s\n]+)([)）])/g,
    (...args) => `(<${args[2].replace(/\\*:\/\//g, '://')}>)`
  );

  content = content?.replace(/<br \/>/g, '\n\n');
  content = content?.replace(/\\n/g, '\n');
  content = content?.replace(/\\r/g, '');
  if (isReferenceList) {
    content += '\n';
    for (const footnote of footnotes) {
      content += `[^${footnote.index}]`;
    }
  } else if (referenceList.length > 0) {
    content += `\n[^999]`;
  }
  const renderFootnote = () => {
    if (!footnotes.length) {
      return '';
    }
    return (
      '\n\n' + footnotes.map((footnote) => `[^${footnote.index}]: ${footnote.title || footnote.source}`).join('\n')
    );
  };

  return (
    <FootnoteContext.Provider
      value={{
        footnotes,
        referenceList,
        isReferenceList,
        isDigitalHuman: props.isDigitalHuman,
      }}
    >
      <ReferenceContext.Provider value={{ getReference, clickReference }}>
        <section
          className={cx(
            'prose dark:prose-invert',
            props.isDigitalHuman && `text-white ${classes['digitalHuman']}`,
            classes['highlightContainer'],
            classes['fullWidth'],
            props.className
          )}
          style={{ wordBreak: 'break-word' }}
        >
          <PhotoProvider maskOpacity={0.6}>
            <ReactMarkdown
              remarkPlugins={[RemarkMath, RemarkGfm, RemarkBreaks]}
              rehypePlugins={[
                RehypeKatex,
                [
                  RehypeHighlight,
                  {
                    detect: false,
                    ignoreMissing: true,
                  },
                ],
              ]}
              components={{
                code: CodeComponent,
                img: ImageComponent,
                a: LinkComponent,
                section: FootnoteComponent,
                sup: SupComponent,
                h1: 'h3',
                h2: 'h3',
              }}
              linkTarget={'_blank'}
            >
              {`${content}${renderFootnote()}`}
            </ReactMarkdown>
          </PhotoProvider>
        </section>
      </ReferenceContext.Provider>
    </FootnoteContext.Provider>
  );
}
